import React, { useEffect, useMemo, useState } from "react";
import { AxiosError } from "axios";
import { queryCache, QueryKey, useMutation } from "react-query";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  Button,
  Dialog,
  Loader,
  showNotification,
} from "fe-shared/src/components";
import useDialog from "fe-shared/src/hooks/useDialog";
import { CheckIcon } from "fe-shared/src/components/Icons";
import { ApiError } from "fe-shared/src/types/common";
import { Transfer, TransferType } from "../../../types";
import { DialogHeader, InvalidationControls } from "../..";
import useInvalidationControls from "../../../hooks/useInvalidationControls";

import { actions, ActionConfig, TransferAction } from "../modules/config";
import * as api from "../modules/api";
import styles from "../TransferDetails.module.scss";

interface Props {
  transfer: Transfer;
  isStaffView?: boolean;
  canCancelTransfer?: boolean;
  canInvalidateTransfer?: boolean;
  activitiesQueryKey?: QueryKey;
  transferQueryKey?: QueryKey;
}

type ToastIcons = {
  [key in TransferAction]?: React.ElementType;
};

const { Cancel, Invalidate } = TransferAction;

const toastIcons: ToastIcons = {
  [Cancel]: CheckIcon,
  [Invalidate]: CheckIcon,
};

const TransferActions = (props: Props) => {
  const {
    transfer,
    isStaffView,
    canCancelTransfer,
    canInvalidateTransfer,
    activitiesQueryKey,
    transferQueryKey,
  } = props;

  const history = useHistory();
  const { t } = useTranslation();
  const [activeAction, setActiveAction] = useState(actions[0]);
  const {
    isReasonIncorrect,
    invalidationReason,
    isShowToParticipant,
    ...invalidationProps
  } = useInvalidationControls();
  const isInvalidationAction = activeAction.name === Invalidate;
  const isWriteOff = [
    TransferType.NonEligibleWriteOff,
    TransferType.NonEligibleBufferWriteOff,
  ].includes(transfer.transferType);

  const {
    isDialogRendered,
    isDialogActive,
    openDialog,
    closeDialog,
  } = useDialog();

  const { name: activeActionName } = activeAction;

  const visibleActions = useMemo(() => {
    const permissions = {
      [Cancel]: canCancelTransfer,
      [Invalidate]: canInvalidateTransfer,
    };

    return actions.filter((action) =>
      action.isVisible(transfer, permissions, isStaffView)
    );
  }, [transfer, canCancelTransfer, canInvalidateTransfer, isStaffView]);

  const getMutationConfig = (action: TransferAction) => ({
    onSuccess: () => {
      const iconComponent = toastIcons[action];

      queryCache.invalidateQueries(transferQueryKey);
      queryCache.invalidateQueries(activitiesQueryKey);
      showNotification(
        t(`transfers.actions.${action}.toastMessage`),
        undefined,
        iconComponent
      );
    },
  });

  const mutations = {
    [Cancel]: useMutation<unknown, AxiosError<ApiError>>(
      () =>
        api.cancelTransfer({
          isWriteOff,
          transferNumber: transfer.transferNumber,
        }),
      {
        onSuccess: () => {
          getMutationConfig(Cancel).onSuccess();
          history.push("/transfers");
        },
      }
    ),
    [Invalidate]: useMutation<unknown, AxiosError<ApiError>>(
      () =>
        api.invalidateTransfer({
          invalidationReason,
          isShowToParticipant,
          transferNumber: transfer.transferNumber,
        }),
      getMutationConfig(Invalidate)
    ),
  };

  const [applyAction, { isLoading, isSuccess }] = mutations[activeActionName];

  const dialogTitle = (
    <DialogHeader
      type={transfer.transferType}
      title={t(`transfers.actions.${activeActionName}.title`)}
      number={transfer.transferNumber}
      translationRoot="transfers"
    />
  );

  const dialogActions = [
    <Button
      key="1"
      id={`transfer-action-dialog-${activeActionName}-button`}
      onClick={() => applyAction()}
      disabled={isInvalidationAction && isReasonIncorrect}
      {...activeAction?.buttonProps}
      {...activeAction?.dialogButtonProps}
    >
      {t(`transfers.actions.${activeActionName}.modalbuttonTitle`)}
    </Button>,
    <Button
      key="2"
      variant="outlined"
      onClick={closeDialog}
      id="transfer-action-dialog-cancel-button"
    >
      {t("cancel")}
    </Button>,
  ];

  useEffect(() => {
    if (!isLoading && isSuccess) {
      closeDialog();
    }
  }, [isLoading, isSuccess, history, closeDialog]);

  const applyDialogAction = (action: ActionConfig) => {
    setActiveAction(action);
    openDialog();
  };

  return visibleActions.length ? (
    <>
      <div className={styles.actions}>
        {visibleActions.map((action) => {
          const { name, buttonProps, hasConfirmationDialog } = action;
          const [applyInstantAction] = mutations[name];

          return (
            <Button
              key={name}
              id={`transfer-action-${name}-button`}
              className={styles.button}
              disabled={isLoading}
              onClick={() => {
                if (hasConfirmationDialog) {
                  applyDialogAction(action);
                } else {
                  applyInstantAction();
                }
              }}
              {...buttonProps}
            >
              {t(`transfers.actions.${name}.buttonLabel`)}
            </Button>
          );
        })}
      </div>
      {isDialogRendered && (
        <Dialog
          modal
          title={dialogTitle}
          actions={dialogActions}
          isActive={isDialogActive}
          onClose={closeDialog}
        >
          {isLoading && <Loader isBlocking />}
          {isInvalidationAction ? (
            <InvalidationControls
              isWithCheckbox
              invalidationReason={invalidationReason}
              isShowToParticipant={isShowToParticipant}
              {...invalidationProps}
            />
          ) : (
            t(`transfers.actions.${activeActionName}.modalMessage`)
          )}
        </Dialog>
      )}
    </>
  ) : null;
};

export default TransferActions;
